import { HEROES } from './heroes';
import { patchLoadout, profileLoadouts } from './meta/peek';
import type { HeroClassId, HeroId } from './types';

/**
 * Class identities and the powerup catalog.
 *
 * Edit this file when Brandt changes a name, a cost, or a blurb.
 * `effect` is the card line. Behavior lives in `src/powerups/<id>.ts`.
 * A catalog id with no module can still be equipped; the duel will not fire it.
 * Grove tricks belong to Fern, Posy, and Dash. They are still not sold for acorns.
 * Universal powerups are offered to every hero.
 */

export interface BadgeStyle {
  name: string;
  /** Glossy pill fill, edge, and shine. */
  fill: number;
  edge: number;
  shine: number;
  /** 2px stroke behind cream type on the pill. */
  stroke: string;
}

export const CLASSES: Record<HeroClassId, BadgeStyle & { id: HeroClassId; blurb: string }> = {
  guardian: {
    id: 'guardian',
    name: 'Guardian',
    blurb: 'Hold the line, then strike.',
    fill: 0x2f8a4a,
    edge: 0x145228,
    shine: 0x8ee0a8,
    stroke: '#145228',
  },
  infiltration: {
    id: 'infiltration',
    name: 'Infiltration',
    blurb: 'Slip under, snare and sap.',
    fill: 0x7a3e86,
    edge: 0x3a1844,
    shine: 0xd9a8e4,
    stroke: '#3A1844',
  },
  stealth: {
    id: 'stealth',
    name: 'Stealth',
    blurb: 'Hide, decoy and mark tiles.',
    fill: 0x3d4f92,
    edge: 0x1a2448,
    shine: 0xa8b8e8,
    stroke: '#1A2448',
  },
  tidecaller: {
    id: 'tidecaller',
    name: 'Tidecaller',
    blurb: 'Pull, lift and wash the glen.',
    fill: 0x1f7f8c,
    edge: 0x0c3a42,
    shine: 0x8ad8e0,
    stroke: '#0C3A42',
  },
  grove: {
    id: 'grove',
    name: 'Grove',
    blurb: 'Sow a storm across the glen.',
    fill: 0xc45a8a,
    edge: 0x6a2044,
    shine: 0xffb4d4,
    stroke: '#6A2044',
  },
};

export const UNIVERSAL: BadgeStyle & { id: 'universal' } = {
  id: 'universal',
  name: 'Universal',
  fill: 0xd0892a,
  edge: 0x6a3e0c,
  shine: 0xffd27a,
  stroke: '#6A3E0C',
};

export type PowerupClass = HeroClassId | 'universal';

export type GlyphName =
  | 'plus'
  | 'dots'
  | 'drop'
  | 'line'
  | 'mail'
  | 'ring'
  | 'swap'
  | 'mask'
  | 'moon'
  | 'trail'
  | 'crack'
  | 'coin'
  | 'root'
  | 'bomb'
  | 'shield'
  | 'burst'
  | 'heart'
  | 'bolt'
  | 'wave'
  | 'bubble'
  | 'rain'
  | 'falls'
  | 'sprout'
  | 'bloom';

export type TrickTier = 'sprout' | 'bramble' | 'oak' | 'moonlit';

export const TIER_NAME: Record<TrickTier, string> = {
  sprout: 'Sprout',
  bramble: 'Bramble',
  oak: 'Oak',
  moonlit: 'Moonlit',
};

export interface PowerupDef {
  id: string;
  name: string;
  cost: number;
  classId: PowerupClass;
  effect: string;
  glyph: GlyphName;
  tier: TrickTier;
  /** Trophy-road stop that grants this trick. */
  unlockAt: number;
  /** Unlimited, never spent, and not a road stop. */
  free?: boolean;
}

/**
 * Costs stay in the 4–12 band. A trick spends one move, so the cost is how
 * fast the disc refills, not a second turn. Wide clears score at half rate
 * and do not chain.
 * Grove tricks are equipped by Grove heroes. They are granted on the road, not sold.
 */
export const POWERUPS = [
  { id: 'quill-burst', name: 'Quill Burst', cost: 4, classId: 'guardian', tier: 'sprout', unlockAt: 0, effect: 'Pop a tile and one diagonal neighbor.', glyph: 'burst' },
  { id: 'dandelion-drift', name: 'Dandelion Drift', cost: 6, classId: 'universal', tier: 'sprout', unlockAt: 6, effect: 'Clears 2 random tiles.', glyph: 'dots' },
  { id: 'honey-drip', name: 'Honey Drip', cost: 6, classId: 'universal', tier: 'sprout', unlockAt: 12, effect: '2 tiles become your color.', glyph: 'drop' },
  { id: 'thicket-shield', name: 'Thicket Shield', cost: 6, classId: 'guardian', tier: 'sprout', unlockAt: 18, effect: 'Block one rival hit, warm your Charm meter, clear 2.', glyph: 'shield' },
  { id: 'pilfer', name: 'Pilfer', cost: 4, classId: 'infiltration', tier: 'sprout', unlockAt: 26, effect: 'Steal 2 Charm charge and clear 1 tile.', glyph: 'coin' },
  { id: 'undermine', name: 'Undermine', cost: 7, classId: 'infiltration', tier: 'sprout', unlockAt: 32, effect: 'Crack the bottom of a column.', glyph: 'crack' },
  { id: 'moonlit-mark', name: 'Moonlit Mark', cost: 5, classId: 'stealth', tier: 'sprout', unlockAt: 38, effect: 'Your next turns score a marked color extra.', glyph: 'moon' },
  { id: 'vanishing-trail', name: 'Vanishing Trail', cost: 6, classId: 'stealth', tier: 'sprout', unlockAt: 44, effect: 'Clear the tile you tap.', glyph: 'trail' },
  { id: 'riptide', name: 'Riptide', cost: 5, classId: 'tidecaller', tier: 'sprout', unlockAt: 56, effect: 'Shift a row one step.', glyph: 'wave' },
  { id: 'waterfall', name: 'Waterfall', cost: 6, classId: 'tidecaller', tier: 'sprout', unlockAt: 62, effect: 'Clear the heart of a column.', glyph: 'falls' },
  { id: 'bloomstorm', name: 'Bloomstorm', cost: 6, classId: 'grove', tier: 'sprout', unlockAt: 68, effect: 'Burst up to 4 blossoms.', glyph: 'bloom' },
  { id: 'pollen-puff', name: 'Pollen Puff', cost: 5, classId: 'grove', tier: 'sprout', unlockAt: 74, effect: 'Clear 2 tiles, blossoms first.', glyph: 'dots' },

  { id: 'rootsnare', name: 'Rootsnare', cost: 6, classId: 'infiltration', tier: 'bramble', unlockAt: 90, effect: 'Root 6 tiles and clear one.', glyph: 'root' },
  { id: 'hearthstone', name: 'Hearthstone', cost: 7, classId: 'guardian', tier: 'bramble', unlockAt: 115, effect: 'Warm your Charm meter a little and clear a tile.', glyph: 'heart' },
  { id: 'mushroom-ring', name: 'Mushroom Ring', cost: 8, classId: 'universal', tier: 'bramble', unlockAt: 140, effect: 'Clears two tiles beside the one you tap.', glyph: 'ring' },
  { id: 'sapper-charge', name: 'Sapper Charge', cost: 6, classId: 'infiltration', tier: 'bramble', unlockAt: 170, effect: 'Plant a 3×3 that opens your next turn.', glyph: 'bomb' },
  { id: 'seedling', name: 'Seedling', cost: 6, classId: 'grove', tier: 'bramble', unlockAt: 195, effect: 'Grow a shielded bomb.', glyph: 'sprout' },
  { id: 'fox-mask', name: 'Fox Mask', cost: 6, classId: 'stealth', tier: 'bramble', unlockAt: 245, effect: 'Mask 4 tiles as decoys that pay you.', glyph: 'mask' },
  { id: 'snail-mail', name: 'Snail Mail', cost: 8, classId: 'universal', tier: 'bramble', unlockAt: 270, effect: '+1 move, repaid next turn, and clear a tile.', glyph: 'mail' },
  { id: 'lantern-ward', name: 'Lantern Ward', cost: 7, classId: 'guardian', tier: 'bramble', unlockAt: 300, effect: 'Shield a tile, warm the Charm meter, clear a neighbor.', glyph: 'heart' },
  { id: 'shadow-swap', name: 'Shadow Swap', cost: 10, classId: 'stealth', tier: 'bramble', unlockAt: 330, effect: 'Swap any two tiles into a short line.', glyph: 'swap' },
  { id: 'rainwash', name: 'Rainwash', cost: 8, classId: 'tidecaller', tier: 'bramble', unlockAt: 355, effect: 'Wash up to 2 tiles of one color into another.', glyph: 'rain' },
  { id: 'burrow-seed', name: 'Burrow Seed', cost: 8, classId: 'grove', tier: 'bramble', unlockAt: 375, effect: 'A shielded seed grows into a row stripe.', glyph: 'sprout' },
  { id: 'bubble-lift', name: 'Bubble Lift', cost: 7, classId: 'tidecaller', tier: 'bramble', unlockAt: 395, effect: 'Lift 3 tiles. New ones drop.', glyph: 'bubble' },

  { id: 'rolling-thunder', name: 'Rolling Thunder', cost: 10, classId: 'guardian', tier: 'oak', unlockAt: 420, effect: 'Clear two rows.', glyph: 'bolt' },
  { id: 'firefly-line', name: 'Firefly Line', cost: 8, classId: 'universal', tier: 'oak', unlockAt: 445, effect: 'Clears 4 tiles in a chosen row.', glyph: 'line' },
  { id: 'sinkhole', name: 'Sinkhole', cost: 10, classId: 'infiltration', tier: 'oak', unlockAt: 470, effect: 'Clear a column. A shield can cancel it.', glyph: 'crack' },
  { id: 'bramble-bulwark', name: 'Bramble Bulwark', cost: 9, classId: 'guardian', tier: 'oak', unlockAt: 500, effect: 'Shield a row and clear the row beside it.', glyph: 'shield' },
  { id: 'acorn-pop', name: 'Acorn Pop', cost: 10, classId: 'universal', tier: 'oak', unlockAt: 525, effect: 'Clears a short plus.', glyph: 'plus' },
  { id: 'tripwire', name: 'Tripwire', cost: 9, classId: 'infiltration', tier: 'oak', unlockAt: 550, effect: 'Lock 4 of their fullest color and steal charge.', glyph: 'root' },
  { id: 'double-bluff', name: 'Double Bluff', cost: 10, classId: 'stealth', tier: 'oak', unlockAt: 580, effect: 'Swap two tiles into a short line of decoys.', glyph: 'swap' },
  { id: 'spring-tide', name: 'Spring Tide', cost: 10, classId: 'tidecaller', tier: 'oak', unlockAt: 605, effect: 'Shift a row two steps.', glyph: 'wave' },
  { id: 'thorn-canopy', name: 'Thorn Canopy', cost: 10, classId: 'grove', tier: 'oak', unlockAt: 630, effect: 'Grow three shielded bombs.', glyph: 'bomb' },
  { id: 'night-pocket', name: 'Night Pocket', cost: 9, classId: 'stealth', tier: 'oak', unlockAt: 670, effect: 'Disguise 6 tiles of a color so they pay you.', glyph: 'mask' },
  { id: 'undertow', name: 'Undertow', cost: 11, classId: 'tidecaller', tier: 'oak', unlockAt: 700, effect: 'Lift a whole column.', glyph: 'falls' },
  { id: 'petal-gale', name: 'Petal Gale', cost: 9, classId: 'grove', tier: 'oak', unlockAt: 730, effect: 'Clear up to 6 blossoms in a row.', glyph: 'bloom' },

  { id: 'last-light', name: 'Last Light', cost: 12, classId: 'guardian', tier: 'moonlit', unlockAt: 785, effect: 'Two shields, a warm Charm meter, and a small plus.', glyph: 'shield' },
  { id: 'acorn-crown', name: 'Acorn Crown', cost: 12, classId: 'universal', tier: 'moonlit', unlockAt: 820, effect: 'Clear a 3×3.', glyph: 'plus' },
  { id: 'crown-snare', name: 'Crown Snare', cost: 12, classId: 'infiltration', tier: 'moonlit', unlockAt: 855, effect: 'Lock 8 tiles and steal charge.', glyph: 'root' },
  { id: 'masquerade', name: 'Masquerade', cost: 12, classId: 'stealth', tier: 'moonlit', unlockAt: 885, effect: 'Mask 8 of your tiles as decoys.', glyph: 'mask' },
  { id: 'moon-tide', name: 'Moon Tide', cost: 12, classId: 'tidecaller', tier: 'moonlit', unlockAt: 920, effect: 'Shift every row one step, alternating.', glyph: 'wave' },
  { id: 'bloom-crown', name: 'Bloom Crown', cost: 12, classId: 'grove', tier: 'moonlit', unlockAt: 945, effect: 'Plant blossoms, one of them a bomb.', glyph: 'bloom' },
  { id: 'quill-coronet', name: 'Quill Coronet', cost: 14, classId: 'guardian', tier: 'moonlit', unlockAt: 970, effect: 'Clear three rows.', glyph: 'bolt' },
  { id: 'story-lantern', name: 'Story Lantern', cost: 14, classId: 'universal', tier: 'moonlit', unlockAt: 995, effect: 'Repaint up to 6 tiles of a color as yours.', glyph: 'drop' },
  { id: 'midnight-sap', name: 'Midnight Sap', cost: 14, classId: 'infiltration', tier: 'moonlit', unlockAt: 1020, effect: 'Clear a column and lock the columns beside it.', glyph: 'crack' },
  { id: 'moonwell', name: 'Moonwell', cost: 14, classId: 'stealth', tier: 'moonlit', unlockAt: 1045, effect: 'Swap any two tiles into a scored line.', glyph: 'moon' },
  { id: 'falls-crown', name: 'Falls Crown', cost: 14, classId: 'tidecaller', tier: 'moonlit', unlockAt: 1070, effect: 'Clear a column and repaint its neighbors.', glyph: 'falls' },
  { id: 'moon-orchard', name: 'Moon Orchard', cost: 14, classId: 'grove', tier: 'moonlit', unlockAt: 1090, effect: 'Clear blossoms, then plant and shield new ones.', glyph: 'bloom' },

  { id: 'dewdrop', name: 'Dewdrop', cost: 6, classId: 'universal', tier: 'sprout', unlockAt: 36, effect: 'Charm meter +6.', glyph: 'drop' },
  { id: 'beehive-drop', name: 'Beehive Drop', cost: 8, classId: 'universal', tier: 'bramble', unlockAt: 210, effect: 'Place a bee bomb.', glyph: 'bomb' },
  { id: 'burrow-hop', name: 'Burrow Hop', cost: 10, classId: 'universal', tier: 'oak', unlockAt: 510, effect: 'Move any tile anywhere.', glyph: 'swap' },
  { id: 'thorn-cross', name: 'Thorn Cross', cost: 8, classId: 'universal', tier: 'moonlit', unlockAt: 800, effect: 'Clear a plus at reduced points.', glyph: 'plus' },
  { id: 'pinecone-mallet', name: 'Pinecone Mallet', cost: 1, classId: 'universal', tier: 'sprout', unlockAt: -1, free: true, effect: 'Remove 1 tile. Always in the bag.', glyph: 'burst' },
  { id: 'leaf-gust', name: 'Leaf Gust', cost: 1, classId: 'universal', tier: 'sprout', unlockAt: -2, free: true, effect: 'Shuffle the board. Always in the bag.', glyph: 'dots' },
] as const satisfies readonly PowerupDef[];

export type PowerupId = (typeof POWERUPS)[number]['id'];

const BY_ID = new Map<string, PowerupDef>(POWERUPS.map((power) => [power.id, power]));

export function powerupById(id: string): PowerupDef | undefined {
  return BY_ID.get(id);
}

/** `as const` entries omit `free`, so the flag is read through the catalog type. */
export function powerIsFree(power: { id: string }): boolean {
  return powerupById(power.id)?.free === true;
}

export function badgeOf(classId: PowerupClass): BadgeStyle {
  if (classId === 'universal') return UNIVERSAL;
  return CLASSES[classId];
}

/** Scroll line on hero select, e.g. `Stealth: Hide, decoy and mark tiles.` */
export function classLine(classId: HeroClassId): string {
  const info = CLASSES[classId];
  return `${info.name}: ${info.blurb}`;
}

/** Class powerups first, then Universal. Grove heroes receive the Grove catalog. */
export function offersFor(heroId: HeroId): PowerupDef[] {
  const classId = HEROES[heroId].classId;
  const mine = POWERUPS.filter((power) => power.classId === classId);
  const shared = POWERUPS.filter((power) => power.classId === 'universal');
  return [...mine, ...shared];
}

export const MAX_LOADOUT = 2;

/**
 * Opening kit for the rival, and the pair a critter gate fills in
 * once those stops are already reached. Pip's first duel holds Quill Burst
 * only. Thicket Shield joins at trophy 18. Rolling Thunder is an Oak at 420.
 */
const OPENING_KIT: Record<HeroId, readonly PowerupId[]> = {
  pip: ['quill-burst', 'thicket-shield'],
  bruno: ['quill-burst', 'thicket-shield'],
  tuck: ['quill-burst', 'thicket-shield'],
  bramble: ['pilfer', 'undermine'],
  thistle: ['pilfer', 'undermine'],
  marlow: ['pilfer', 'undermine'],
  cleo: ['moonlit-mark', 'vanishing-trail'],
  wren: ['moonlit-mark', 'vanishing-trail'],
  juniper: ['moonlit-mark', 'vanishing-trail'],
  otto: ['riptide', 'waterfall'],
  lumi: ['riptide', 'waterfall'],
  barley: ['riptide', 'waterfall'],
  fern: ['bloomstorm', 'pollen-puff'],
  posy: ['bloomstorm', 'pollen-puff'],
  dash: ['bloomstorm', 'pollen-puff'],
};

export function autoLoadout(heroId: HeroId): PowerupId[] {
  return [...OPENING_KIT[heroId]];
}

/** Drop anything outside this hero's class and Universal, then keep the first two. */
export function normalizeLoadout(heroId: HeroId, ids: readonly string[]): PowerupId[] {
  const allowed = new Set(offersFor(heroId).map((power) => power.id));
  const chosen: PowerupId[] = [];
  for (const id of ids) {
    if (!allowed.has(id as PowerupId)) continue;
    if (chosen.includes(id as PowerupId)) continue;
    chosen.push(id as PowerupId);
    if (chosen.length === MAX_LOADOUT) break;
  }
  return chosen;
}

export const LOADOUT_SAVE_KEY = 'critter-clatter.loadouts';

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function readBag(store: KeyValueStore): Partial<Record<HeroId, unknown>> | null {
  const raw = store.getItem(LOADOUT_SAVE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Partial<Record<HeroId, unknown>>;
  } catch {
    return null;
  }
}

function idsFrom(value: unknown, heroId: HeroId): PowerupId[] {
  if (!Array.isArray(value)) return autoLoadout(heroId);
  const ids = value.filter((item): item is string => typeof item === 'string');
  return normalizeLoadout(heroId, ids);
}

/** Saved picks for this hero, or the automatic pair when nothing has been stored yet. */
export function readLoadout(store: KeyValueStore, heroId: HeroId): PowerupId[] {
  const fromProfile = profileLoadouts(store);
  if (fromProfile) {
    if (!Object.prototype.hasOwnProperty.call(fromProfile, heroId)) return autoLoadout(heroId);
    return idsFrom(fromProfile[heroId], heroId);
  }
  const bag = readBag(store);
  if (!bag || !Object.prototype.hasOwnProperty.call(bag, heroId)) return autoLoadout(heroId);
  return idsFrom(bag[heroId], heroId);
}

export function writeLoadout(store: KeyValueStore, heroId: HeroId, ids: readonly string[]): PowerupId[] {
  const bag = readBag(store) ?? {};
  const next = normalizeLoadout(heroId, ids);
  bag[heroId] = next;
  store.setItem(LOADOUT_SAVE_KEY, JSON.stringify(bag));
  patchLoadout(store, heroId, next);
  return next;
}
